import React, { useState, useEffect, useCallback } from 'react';
import { validateService } from '../services/validateService';
import './ValidateResults.css';

/**
 * ValidateResults - VALIDATE Feature Results Component
 * Displays vulnerability validation results with proof of exploitability
 * DriftAlarm.AI - Built by Security Experts
 */

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'];

const ValidateResults = ({ jobId, scanId, onClose }) => {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedSections, setExpandedSections] = useState({
    targetProfile: true,
    r3dteamBrief: true,
    confirmed: true,
    notVulnerable: false,
    inconclusive: false,
    skipped: false
  });
  const [expandedHttpTransactions, setExpandedHttpTransactions] = useState({});
  const [expandedBodies, setExpandedBodies] = useState({});
  const [inconclusiveLimit, setInconclusiveLimit] = useState(25);

  const fetchResults = useCallback(async () => {
    if (!jobId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await validateService.getValidateResult(jobId);
      setResults(data);
      setLoading(false);
    } catch (err) {
      console.error('[ValidateResults] Error fetching:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  const toggleItem = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const toggleHttpTransaction = (id, part) => {
    const key = `${id}-${part}`;
    setExpandedHttpTransactions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const downloadRawResults = () => {
    if (!results) return;
    const dataStr = JSON.stringify(results, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `validate-results-${jobId}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadRawArchive = async () => {
    try {
      const response = await validateService.downloadRawResults(jobId);
      if (response.downloadUrl) {
        window.open(response.downloadUrl, '_blank');
      } else {
        console.error('No download URL returned');
        alert('Download not available. Raw results may not have been uploaded.');
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
      alert('Failed to get download link. Please try again later.');
    }
  };

  const getSeverityClass = (severity) => {
    if (!severity) return 'info';
    return severity.toLowerCase();
  };

  const sortBySeverity = (items) => {
    if (!items) return [];
    return [...items].sort((a, b) => {
      const aIndex = SEVERITY_ORDER.indexOf((a.severity || 'info').toLowerCase());
      const bIndex = SEVERITY_ORDER.indexOf((b.severity || 'info').toLowerCase());
      return aIndex - bIndex;
    });
  };

  // Group items by type for summary display
  const groupByType = (items) => {
    const groups = {};
    items.forEach(item => {
      const type = item.type || 'unknown';
      if (!groups[type]) groups[type] = 0;
      groups[type]++;
    });
    return groups;
  };

  // Format type counts for display
  const formatTypeCounts = (groups) => {
    return Object.entries(groups)
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => `${count} ${type}`)
      .join(', ');
  };

  // Render formatted key-value display for a finding
  const renderFormattedDetails = (item, itemId) => {
    const fields = [
      { key: 'type', label: 'Type' },
      { key: 'status', label: 'Status' },
      { key: 'severity', label: 'Severity' },
      { key: 'target', label: 'Target' },
      { key: 'parameter', label: 'Parameter' },
      { key: 'payload', label: 'Payload', isCode: true },
      { key: 'evidence', label: 'Evidence' },
      { key: 'message', label: 'Message' },
      { key: 'missingHeaders', label: 'Missing Headers', isArray: true },
      { key: 'weakHeaders', label: 'Weak Headers', isArray: true },
      { key: 'description', label: 'Description' },
      { key: 'remediation', label: 'Remediation' },
      { key: 'cve', label: 'CVE', isLink: true }
    ];

    return (
      <div className="formatted-details">
        {fields.map(({ key, label, isCode, isArray, isLink }) => {
          const value = item[key];
          if (!value || (Array.isArray(value) && value.length === 0)) return null;

          return (
            <div key={key} className="detail-field">
              <span className="field-label">{label}:</span>
              {isArray ? (
                <span className="field-value array-value">
                  {value.join(', ')}
                </span>
              ) : isCode ? (
                <code className="field-value code-value">{value}</code>
              ) : isLink && key === 'cve' ? (
                <a
                  href={`https://nvd.nist.gov/vuln/detail/${value}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="field-value cve-link"
                >
                  {value}
                </a>
              ) : (
                <span className="field-value">{value}</span>
              )}
            </div>
          );
        })}

        {/* HTTP Transaction Section */}
        {item.httpTransaction && (
          <div className="http-transaction-section">
            <div className="transaction-header">HTTP Transaction</div>

            {/* Request */}
            <div className="transaction-subsection">
              <div
                className="subsection-header clickable"
                onClick={() => toggleHttpTransaction(itemId, 'request')}
              >
                <span>Request</span>
                <span className="expand-icon">
                  {expandedHttpTransactions[`${itemId}-request`] ? '▼' : '▶'}
                </span>
              </div>
              {expandedHttpTransactions[`${itemId}-request`] && (
                <div className="subsection-content">
                  <div className="detail-field">
                    <span className="field-label">Method:</span>
                    <span className="field-value">{item.httpTransaction.request?.method}</span>
                  </div>
                  <div className="detail-field">
                    <span className="field-label">URL:</span>
                    <span className="field-value url-value">{item.httpTransaction.request?.url}</span>
                  </div>
                  {item.httpTransaction.request?.raw && (
                    <div className="raw-content">
                      <pre>{item.httpTransaction.request.raw}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Response */}
            <div className="transaction-subsection">
              <div
                className="subsection-header clickable"
                onClick={() => toggleHttpTransaction(itemId, 'response')}
              >
                <span>Response</span>
                <span className="expand-icon">
                  {expandedHttpTransactions[`${itemId}-response`] ? '▼' : '▶'}
                </span>
              </div>
              {expandedHttpTransactions[`${itemId}-response`] && (
                <div className="subsection-content">
                  <div className="detail-field">
                    <span className="field-label">Status:</span>
                    <span className="field-value">{item.httpTransaction.response?.status}</span>
                  </div>
                  <div className="detail-field">
                    <span className="field-label">Time:</span>
                    <span className="field-value">{item.httpTransaction.response?.timeMs}ms</span>
                  </div>
                  {item.httpTransaction.response?.headers && (
                    <div className="raw-content headers">
                      <div className="content-label">Headers:</div>
                      <pre>{item.httpTransaction.response.headers}</pre>
                    </div>
                  )}
                  {item.httpTransaction.response?.body && (
                    <div className="raw-content body">
                      <div className="content-label">
                        Body ({Math.round(item.httpTransaction.response.body.length / 1024)}KB):
                        {item.httpTransaction.response.body.length > 5000 && (
                          <button
                            className="show-more-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedBodies(prev => ({...prev, [itemId]: !prev[itemId]}));
                            }}
                          >
                            {expandedBodies[itemId] ? 'Show Less' : 'Show Full'}
                          </button>
                        )}
                      </div>
                      <pre>
                        {expandedBodies[itemId]
                          ? item.httpTransaction.response.body
                          : item.httpTransaction.response.body.substring(0, 5000) +
                            (item.httpTransaction.response.body.length > 5000 ? '\n... [truncated]' : '')}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="validate-results-container">
        <div className="validate-loading">
          <div className="validate-spinner"></div>
          <p>Loading validation results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="validate-results-container">
        <div className="validate-error">
          <span className="error-icon">!</span>
          <p>{error}</p>
          <button onClick={fetchResults}>Retry</button>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="validate-results-container">
        <div className="validate-empty">
          <p>No validation results found.</p>
        </div>
      </div>
    );
  }

  // Transform API response to component format
  const allValidations = results.validations || [];
  const validated = allValidations.filter(v => v.status === 'confirmed');
  const not_vulnerable = allValidations.filter(v => v.status === 'not_exploitable');
  const failed = allValidations.filter(v => v.status === 'inconclusive');
  const skipped = allValidations.filter(v => v.status === 'skipped' || v.status === 'error');

  // Group inconclusive by type for summary
  const inconclusiveGroups = groupByType(failed);

  // Get discovery metrics
  const discovery = results.discovery || {};
  const metrics = results.metrics || {};
  const urlCount = discovery.urls?.length || metrics.urlsScanned || 0;
  const ipCount = discovery.ips?.length || metrics.ipsScanned || 0;
  const portCount = discovery.ports?.length || 0;

  // Risk level from summary
  const riskLevel = results.summary?.riskLevel || 'unknown';

  // AI Commentary
  const aiCommentary = results.aiCommentary || null;

  return (
    <div className="validate-results-container">
      {/* =====================================================
          TARGET PROFILE SECTION
          ===================================================== */}
      <div className="target-profile-section">
        <div
          className="section-header-bar clickable"
          onClick={() => toggleSection('targetProfile')}
        >
          <div className="header-left">
            <span className="header-icon">🎯</span>
            <span className="header-title">TARGET PROFILE</span>
          </div>
          <span className="expand-toggle">
            {expandedSections.targetProfile ? '▼' : '▶'}
          </span>
        </div>

        {expandedSections.targetProfile && (
          <div className="target-profile-content">
            {/* Primary Info Grid - 4 columns */}
            <div className="profile-grid">
              <div className="profile-item">
                <span className="profile-label">Target</span>
                <span className="profile-value mono">{results.target || 'N/A'}</span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Mode</span>
                <span className={`profile-value mode-badge ${results.mode}`}>
                  {results.mode === 'integrated' ? 'DEEP SCAN' : 'Standalone'}
                </span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Discovery Scope</span>
                <span className="profile-value scope-value">
                  {urlCount} URLs, {ipCount} IPs, {portCount} Ports
                </span>
              </div>
              <div className="profile-item">
                <span className="profile-label">Risk Level</span>
                <span className={`profile-value risk-badge ${riskLevel}`}>
                  {riskLevel.toUpperCase()}
                </span>
              </div>
            </div>

            {/* Statistics Panel */}
            <div className="stats-panel">
              <div className="stat-item confirmed">
                <span className="stat-count">{validated.length}</span>
                <span className="stat-label">Confirmed</span>
              </div>
              <div className="stat-item not-exploitable">
                <span className="stat-count">{not_vulnerable.length}</span>
                <span className="stat-label">Not Exploitable</span>
              </div>
              <div className="stat-item inconclusive">
                <span className="stat-count">{failed.length}</span>
                <span className="stat-label">Inconclusive</span>
              </div>
              <div className="stat-item skipped">
                <span className="stat-count">{skipped.length}</span>
                <span className="stat-label">Skipped</span>
              </div>
            </div>

            {/* Source Info for Integrated Mode */}
            {results.mode === 'integrated' && results.sourceType && (
              <div className="source-info">
                <span className="source-label">Source:</span>
                <span className="source-type">{results.sourceType}</span>
                {results.sourceScanId && (
                  <span className="source-id mono">{results.sourceScanId}</span>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="profile-actions">
              <button className="download-btn" onClick={downloadRawResults}>
                Download Raw JSON
              </button>
              {results.artifacts?.rawArchiveBlob && (
                <button className="download-btn archive" onClick={downloadRawArchive}>
                  Download Full Archive
                </button>
              )}
              <span className="job-id">Job ID: {jobId}</span>
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          DriftAlarm BRIEF SECTION
          ===================================================== */}
      {aiCommentary && (
        <div className="r3dteam-brief-section">
          <div
            className="section-header-bar brief-header clickable"
            onClick={() => toggleSection('r3dteamBrief')}
          >
            <div className="header-left">
              <span className="r3dteam-badge">DriftAlarm BRIEF</span>
              {aiCommentary.riskAssessment && (
                <span className={`risk-assessment-badge ${aiCommentary.riskAssessment.toLowerCase()}`}>
                  {aiCommentary.riskAssessment}
                </span>
              )}
            </div>
            <span className="expand-toggle">
              {expandedSections.r3dteamBrief ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.r3dteamBrief && (
            <div className="brief-content">
              {/* Executive Summary - check both field names */}
              {(aiCommentary.executiveSummary || aiCommentary.summary) && (
                <div className="brief-summary">
                  <p>{aiCommentary.executiveSummary || aiCommentary.summary}</p>
                </div>
              )}

              {/* Recommendations */}
              {aiCommentary.recommendations && aiCommentary.recommendations.length > 0 && (
                <div className="brief-section">
                  <h4 className="brief-section-title">Recommendations</h4>
                  <ul className="brief-list">
                    {aiCommentary.recommendations.map((rec, idx) => (
                      <li key={idx}>{rec}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Immediate Actions */}
              {aiCommentary.immediateActions && aiCommentary.immediateActions.length > 0 && (
                <div className="brief-section urgent">
                  <h4 className="brief-section-title">Immediate Actions</h4>
                  <ul className="brief-list urgent">
                    {aiCommentary.immediateActions.map((action, idx) => (
                      <li key={idx}>{action}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* =====================================================
          CONFIRMED VULNERABILITIES SECTION
          ===================================================== */}
      {validated.length > 0 && (
        <div className="validate-section confirmed-section">
          <div
            className="section-header-bar confirmed-header clickable"
            onClick={() => toggleSection('confirmed')}
          >
            <div className="header-left">
              <span className="section-icon confirmed">!</span>
              <span className="section-title-text">
                Confirmed Exploitable ({validated.length})
              </span>
            </div>
            <span className="expand-toggle">
              {expandedSections.confirmed ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.confirmed && (
            <>
              <p className="section-desc">
                These vulnerabilities have been validated as exploitable. Immediate remediation recommended.
              </p>
              <div className="validate-items">
                {sortBySeverity(validated).map((item, index) => {
                  const itemId = `validated-${index}`;
                  const isExpanded = expandedItems[itemId];
                  return (
                    <div key={itemId} className={`validate-item severity-${getSeverityClass(item.severity)}`}>
                      <div className="item-header" onClick={() => toggleItem(itemId)}>
                        <div className="item-status confirmed">
                          <span>CONFIRMED</span>
                        </div>
                        <div className="item-info">
                          <span className="item-type">{item.type || item.vulnerability_type}</span>
                          <span className={`item-severity severity-${getSeverityClass(item.severity)}`}>
                            {item.severity || 'Unknown'}
                          </span>
                          {item.message && (
                            <span className="item-message">{item.message}</span>
                          )}
                        </div>
                        <span className={`item-expand ${isExpanded ? 'expanded' : ''}`}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                      </div>
                      <div className="item-target">
                        <span className="target-label">Target:</span>
                        <span className="target-value">{item.target || item.url}</span>
                      </div>
                      {isExpanded && (
                        <div className="item-details">
                          {renderFormattedDetails(item, itemId)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* =====================================================
          NOT VULNERABLE SECTION
          ===================================================== */}
      {not_vulnerable.length > 0 && (
        <div className="validate-section not-vuln-section">
          <div
            className="section-header-bar not-vuln-header clickable"
            onClick={() => toggleSection('notVulnerable')}
          >
            <div className="header-left">
              <span className="section-icon not-vuln">✓</span>
              <span className="section-title-text">
                Not Vulnerable ({not_vulnerable.length})
              </span>
            </div>
            <span className="expand-toggle">
              {expandedSections.notVulnerable ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.notVulnerable && (
            <>
              <p className="section-desc">
                Initial scan flagged these, but validation confirmed they are not exploitable.
              </p>
              <div className="validate-items collapsed-list">
                {not_vulnerable.map((item, index) => (
                  <div key={`notvuln-${index}`} className="validate-item-compact not-vuln">
                    <span className="compact-type">{item.type || item.vulnerability_type}</span>
                    <span className="compact-target">{item.target || item.url}</span>
                    <span className="compact-reason">{item.reason || item.message || 'Not exploitable'}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* =====================================================
          INCONCLUSIVE SECTION (Optimized)
          ===================================================== */}
      {failed.length > 0 && (
        <div className="validate-section inconclusive-section">
          <div
            className="section-header-bar inconclusive-header clickable"
            onClick={() => toggleSection('inconclusive')}
          >
            <div className="header-left">
              <span className="section-icon inconclusive">?</span>
              <span className="section-title-text">
                Inconclusive ({failed.length})
              </span>
              <span className="type-summary">
                ({formatTypeCounts(inconclusiveGroups)})
              </span>
              <span className="info-tooltip" title="Inconclusive means potential vulnerability patterns were detected but could not be confirmed as exploitable. Manual verification recommended.">
                ℹ️
              </span>
            </div>
            <span className="expand-toggle">
              {expandedSections.inconclusive ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.inconclusive && (
            <>
              <div className="inconclusive-explanation">
                <span className="explanation-icon">ℹ️</span>
                <span className="explanation-text">
                  Inconclusive results indicate potential vulnerability patterns were detected but could not be
                  confirmed as exploitable through automated testing. This commonly occurs with XSS tests where
                  input is reflected but script execution could not be verified. Manual verification is recommended.
                </span>
              </div>
              <div className="validate-items collapsed-list">
                {failed.slice(0, inconclusiveLimit).map((item, index) => (
                  <div key={`failed-${index}`} className="validate-item-compact inconclusive">
                    <span className="compact-type">{item.type || item.vulnerability_type}</span>
                    <span className="compact-target">{item.target || item.url}</span>
                    <span className="compact-reason">{item.message || item.error || item.reason || 'Validation failed'}</span>
                  </div>
                ))}
              </div>
              {failed.length > inconclusiveLimit && (
                <button
                  className="load-more-btn"
                  onClick={() => setInconclusiveLimit(prev => prev + 25)}
                >
                  Load More ({failed.length - inconclusiveLimit} remaining)
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* =====================================================
          SKIPPED SECTION
          ===================================================== */}
      {skipped.length > 0 && (
        <div className="validate-section skipped-section">
          <div
            className="section-header-bar skipped-header clickable"
            onClick={() => toggleSection('skipped')}
          >
            <div className="header-left">
              <span className="section-icon skipped">—</span>
              <span className="section-title-text">
                Skipped ({skipped.length})
              </span>
            </div>
            <span className="expand-toggle">
              {expandedSections.skipped ? '▼' : '▶'}
            </span>
          </div>

          {expandedSections.skipped && (
            <>
              <p className="section-desc">
                These vulnerabilities could not be tested automatically.
              </p>
              <div className="validate-items collapsed-list">
                {skipped.map((item, index) => (
                  <div key={`skipped-${index}`} className="validate-item-compact skipped">
                    <span className="compact-type">{item.type || item.vulnerability_type}</span>
                    <span className="compact-target">{item.target || item.url}</span>
                    <span className="compact-reason">{item.reason || 'Not supported'}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Empty State */}
      {validated.length === 0 && not_vulnerable.length === 0 && failed.length === 0 && (
        <div className="validate-empty-results">
          <div className="empty-icon">V</div>
          <h3>No Vulnerabilities to Validate</h3>
          <p>The initial scan did not find any vulnerabilities that could be validated.</p>
        </div>
      )}

      {/* Footer */}
      <div className="validate-footer">
        <p className="footer-note">
          VALIDATE performs safe, non-destructive testing to confirm exploitability.
          No data was modified or exfiltrated during validation.
        </p>
        <p className="footer-timestamp">
          Completed: {results.completedAt ? new Date(results.completedAt).toLocaleString() : 'N/A'}
        </p>
      </div>
    </div>
  );
};

export default ValidateResults;
